import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { UsageService } from './usage.service';
import { ServiceError, withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';

type Post = Database['public']['Tables']['posts']['Row'];
type PostInsert = Database['public']['Tables']['posts']['Insert'];
type PostUpdate = Database['public']['Tables']['posts']['Update'];

export interface PostFilters {
  organizationId: string;
  status?: Post['status'];
  platform?: string;
  limit?: number;
  offset?: number;
}

export class PostsService {
  private static readonly TABLE = 'posts';
  private static logger = logger.child({ component: 'PostsService' });

  /**
   * Fetch all posts with optional filters (excludes soft-deleted)
   * Phase 5: Optimized to select only needed columns
   */
  static async getAll(filters: PostFilters) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching posts', { filters });

      let query = supabase
        .from(this.TABLE)
        .select('id, content, status, platform, scheduled_for, published_at, created_at, organization_id, created_by, image_url, hashtags, tracking_link_id, deleted_at')
        .eq('organization_id', filters.organizationId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.platform) {
        query = query.eq('platform', filters.platform);
      }

      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      if (filters.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 10) - 1);
      }

      const { data, error } = await query;

      if (error) {
        this.logger.error('Failed to fetch posts', error, { filters });
        throw error;
      }

      this.logger.info('Posts fetched successfully', { 
        count: data?.length || 0,
        filters 
      });

      return data || [];
    }, { operation: 'getAll', table: this.TABLE });
  }

  /**
   * Get a single post by ID (excludes soft-deleted)
   */
  static async getById(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching post by ID', { postId: id });

      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*')
        .eq('id', id)
        .is('deleted_at', null)
        .maybeSingle();

      if (error) {
        this.logger.error('Failed to fetch post', error, { postId: id });
        throw error;
      }

      if (!data) {
        throw ServiceError.notFound('Post', id);
      }

      return data;
    }, { operation: 'getById', table: this.TABLE });
  }

  /**
   * Get scheduled posts for publishing (excludes soft-deleted)
   */
  static async getScheduled(organizationId: string) {
    return withServiceErrorHandling(async () => {
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('id, content, status, platform, scheduled_for, published_at, created_at, organization_id, created_by')
        .eq('organization_id', organizationId)
        .eq('status', 'scheduled')
        .is('deleted_at', null)
        .lte('scheduled_for', now)
        .order('scheduled_for', { ascending: true });

      if (error) {
        this.logger.error('Failed to fetch scheduled posts', error, { organizationId });
        throw error;
      }

      return data || [];
    }, { operation: 'getScheduled', table: this.TABLE });
  }

  /**
   * Create a new post
   */
  static async create(post: PostInsert) {
    return withServiceErrorHandling(async () => {
      // Check usage limits before creating
      if (post.organization_id) {
        const limitCheck = await UsageService.canCreatePost(post.organization_id);
        if (!limitCheck.allowed) {
          throw ServiceError.validation(limitCheck.reason || 'Post limit reached');
        }
      }

      this.logger.info('Creating post', { 
        organizationId: post.organization_id,
        platform: post.platform 
      });

      const { data, error } = await supabase
        .from(this.TABLE)
        .insert(post)
        .select()
        .maybeSingle();

      if (error) {
        this.logger.error('Failed to create post', error, { post });
        throw error;
      }

      this.logger.info('Post created successfully', { postId: data?.id });

      return data;
    }, { operation: 'create', table: this.TABLE });
  }

  /**
   * Update an existing post
   */
  static async update(id: string, updates: PostUpdate) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Updating post', { postId: id });

      const { data, error } = await supabase
        .from(this.TABLE)
        .update(updates)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) {
        this.logger.error('Failed to update post', error, { postId: id, updates });
        throw error;
      }

      this.logger.info('Post updated successfully', { postId: id });

      return data;
    }, { operation: 'update', table: this.TABLE });
  }

  /**
   * Update post with tracking link appended
   */
  static async updateWithTrackingLink(
    postId: string, 
    trackingLinkId: string, 
    trackingUrl: string,
    ctaText: string
  ) {
    return withServiceErrorHandling(async () => {
      // Get current post content
      const post = await this.getById(postId);
      
      // Append tracking link to content
      const updatedContent = `${post.content}\n\n${ctaText} → ${trackingUrl}`;
      
      return this.update(postId, {
        content: updatedContent,
        tracking_link_id: trackingLinkId
      });
    }, { operation: 'updateWithTrackingLink', table: this.TABLE });
  }

  /**
   * Soft delete a post
   */
  static async delete(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Soft deleting post', { postId: id });

      const { error } = await supabase
        .from(this.TABLE)
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        this.logger.error('Failed to soft delete post', error, { postId: id });
        throw error;
      }

      this.logger.info('Post soft deleted successfully', { postId: id });

      return true;
    }, { operation: 'delete', table: this.TABLE });
  }

  /**
   * Get campaign membership for multiple posts
   */
  static async getCampaignMembership(postIds: string[]): Promise<Map<string, { campaignId: string; campaignName: string }>> {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Checking campaign membership for posts', { count: postIds.length });

      interface CampaignJoinResult {
        post_id: string;
        campaign_id: string;
        campaigns: { id: string; name: string } | null;
      }

      const { data, error } = await supabase
        .from('campaign_posts')
        .select('post_id, campaign_id, campaigns(id, name)')
        .in('post_id', postIds);

      if (error) {
        this.logger.error('Failed to check campaign membership', error);
        throw error;
      }

      const membership = new Map<string, { campaignId: string; campaignName: string }>();
      (data as CampaignJoinResult[] || []).forEach(item => {
        if (item.campaigns) {
          membership.set(item.post_id, {
            campaignId: item.campaign_id,
            campaignName: item.campaigns.name
          });
        }
      });

      return membership;
    }, { operation: 'getCampaignMembership', table: this.TABLE });
  }

  /**
   * Bulk soft delete posts (excludes campaign posts and published posts)
   * Returns summary of what was deleted/skipped
   */
  static async bulkDelete(postIds: string[]): Promise<{
    deleted: string[];
    skipped: { campaignPosts: string[]; publishedPosts: string[] };
  }> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Bulk deleting posts', { count: postIds.length });

      // Get posts to check status and campaign membership
      const { data: posts, error: fetchError } = await supabase
        .from(this.TABLE)
        .select('id, status')
        .in('id', postIds)
        .is('deleted_at', null);

      if (fetchError) throw fetchError;

      // Check campaign membership
      const campaignMembership = await this.getCampaignMembership(postIds);

      // Categorize posts
      const publishedPosts = (posts || []).filter(p => p.status === 'published').map(p => p.id);
      const campaignPosts = postIds.filter(id => campaignMembership.has(id));
      const deletableIds = postIds.filter(
        id => !campaignMembership.has(id) && !publishedPosts.includes(id)
      );

      // Perform bulk soft delete
      if (deletableIds.length > 0) {
        const { error: deleteError } = await supabase
          .from(this.TABLE)
          .update({ deleted_at: new Date().toISOString() })
          .in('id', deletableIds);

        if (deleteError) {
          this.logger.error('Bulk delete failed', deleteError);
          throw deleteError;
        }
      }

      this.logger.info('Bulk delete completed', {
        deleted: deletableIds.length,
        skippedCampaign: campaignPosts.length,
        skippedPublished: publishedPosts.length
      });

      return {
        deleted: deletableIds,
        skipped: {
          campaignPosts,
          publishedPosts
        }
      };
    }, { operation: 'bulkDelete', table: this.TABLE });
  }

  /**
   * Bulk update post status (excludes campaign posts and published posts)
   */
  static async bulkUpdateStatus(
    postIds: string[],
    newStatus: 'draft' | 'scheduled'
  ): Promise<{
    updated: string[];
    skipped: { campaignPosts: string[]; publishedPosts: string[] };
  }> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Bulk updating post status', { count: postIds.length, newStatus });

      // Get posts to check status
      const { data: posts, error: fetchError } = await supabase
        .from(this.TABLE)
        .select('id, status')
        .in('id', postIds)
        .is('deleted_at', null);

      if (fetchError) throw fetchError;

      // Check campaign membership
      const campaignMembership = await this.getCampaignMembership(postIds);

      // Categorize posts
      const publishedPosts = (posts || []).filter(p => p.status === 'published').map(p => p.id);
      const campaignPosts = postIds.filter(id => campaignMembership.has(id));
      const updatableIds = postIds.filter(
        id => !campaignMembership.has(id) && !publishedPosts.includes(id)
      );

      // Perform bulk update
      if (updatableIds.length > 0) {
        const { error: updateError } = await supabase
          .from(this.TABLE)
          .update({ status: newStatus })
          .in('id', updatableIds);

        if (updateError) {
          this.logger.error('Bulk status update failed', updateError);
          throw updateError;
        }
      }

      this.logger.info('Bulk status update completed', {
        updated: updatableIds.length,
        skippedCampaign: campaignPosts.length,
        skippedPublished: publishedPosts.length
      });

      return {
        updated: updatableIds,
        skipped: {
          campaignPosts,
          publishedPosts
        }
      };
    }, { operation: 'bulkUpdateStatus', table: this.TABLE });
  }

  /**
   * Publish a post to its designated platform
   */
  static async publish(id: string): Promise<{ success: boolean; platformPostUrl?: string; platformPostId?: string }> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Publishing post', { postId: id });

      const { data, error } = await supabase.functions.invoke('publish-post', {
        body: { postId: id },
      });

      if (error) throw error;

      this.logger.info('Post published', { postId: id });

      return data;
    }, { operation: 'publish', table: this.TABLE });
  }

  /**
   * Check if a post is part of a campaign
   * Campaign posts cannot be published individually
   */
  static async isPartOfCampaign(postId: string): Promise<{
    isInCampaign: boolean;
    campaignId?: string;
    campaignName?: string;
  }> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Checking campaign membership', { postId });

      interface CampaignJoinResult {
        campaign_id: string;
        campaigns: { id: string; name: string } | null;
      }

      const { data } = await supabase
        .from('campaign_posts')
        .select('campaign_id, campaigns(id, name)')
        .eq('post_id', postId)
        .maybeSingle();
      
      const typedData = data as CampaignJoinResult | null;
      
      return {
        isInCampaign: !!typedData,
        campaignId: typedData?.campaign_id,
        campaignName: typedData?.campaigns?.name
      };
    }, { operation: 'isPartOfCampaign', table: this.TABLE });
  }

  /**
   * Get post analytics
   */
  static async getAnalytics(postId: string) {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from('post_analytics')
        .select('*')
        .eq('post_id', postId)
        .order('recorded_at', { ascending: false });

      if (error) {
        this.logger.error('Failed to fetch post analytics', error, { postId });
        throw error;
      }

      return data || [];
    }, { operation: 'getAnalytics' });
  }

  /**
   * Get shareable posts for employee advocacy (excludes soft-deleted)
   */
  static async getShareablePosts(organizationId: string) {
    return withServiceErrorHandling(async () => {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select(`
          id,
          content,
          platform,
          published_at,
          organization_id,
          employee_shares(count)
        `)
        .eq('organization_id', organizationId)
        .eq('is_shareable', true)
        .eq('status', 'published')
        .is('deleted_at', null)
        .order('published_at', { ascending: false });

      if (error) {
        this.logger.error('Failed to fetch shareable posts', error, { organizationId });
        throw error;
      }

      return data || [];
    }, { operation: 'getShareablePosts', table: this.TABLE });
  }

  /**
   * Track employee share
   */
  static async trackEmployeeShare(
    userId: string,
    postId: string,
    organizationId: string,
    platform: 'linkedin' | 'xcom' | 'facebook'
  ) {
    return withServiceErrorHandling(async () => {
      const { error } = await supabase
        .from('employee_shares')
        .insert({
          user_id: userId,
          post_id: postId,
          organization_id: organizationId,
          platform
        });

      if (error && !error.message.includes('duplicate')) {
        this.logger.error('Failed to track share', error, { 
          userId, 
          postId, 
          platform 
        });
        throw error;
      }

      return true;
    }, { operation: 'trackEmployeeShare' });
  }

  /**
   * Enhance post content with organization DNA
   */
  static async enhanceSinglePost(
    content: string,
    organizationId: string,
    platform?: string
  ) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Enhancing post content', { organizationId, platform });

      const { data, error } = await supabase.functions.invoke('enhance-single-post', {
        body: {
          content,
          organizationId,
          platform,
        },
      });

      if (error) {
        this.logger.error('Enhancement failed', error, { organizationId });
        throw error;
      }

      this.logger.info('Enhancement successful', { organizationId });
      return data.content as string;
    }, { operation: 'enhanceSinglePost', table: this.TABLE });
  }
}
